import React, { useEffect, Suspense, lazy } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import "./App.css";
import Layout from "./layout/layout";
import { jwtDecode } from "jwt-decode";
import { refreshtoken } from "./components/Apis";
import { generateToken, messaging } from "./notifications/firebase";
import { onMessage } from "firebase/messaging";
import toast, { Toaster } from "react-hot-toast";
import { CircularProgress, Box } from "@mui/material";

// ⚡ LAZY LOADING - Load components only when needed
// Keep Login eagerly loaded (first page users see)
import Login from "./pages/dashboard/login";

// Lazy load all dashboard components
const Dashboard = lazy(() => import("./pages/dashboard/Dashboard"));
const Maintenance = lazy(() => import("./pages/dashboard/Maintenance/Maintenance"));
const Users = lazy(() => import("./pages/dashboard/Users/Users"));
const Analysis = lazy(() => import("./pages/dashboard/Analysis/Analysis"));
const Owners = lazy(() => import("./pages/dashboard/Owners/Owners"));
const Requests = lazy(() => import("./pages/dashboard/Requests/Requests"));
const Units = lazy(() => import("./pages/dashboard/Units/Units"));
const Payment = lazy(() => import("./pages/dashboard/Payment/Payment"));
const ContactUs = lazy(() => import("./pages/dashboard/ContactUs/ContactUs"));
const ChangePassword = lazy(() => import("./pages/dashboard/ChangePassword/ChangePawword"));
const Profile = lazy(() => import("./pages/dashboard/Profile/Profile"));
const User = lazy(() => import("./pages/dashboard/Users/user"));
const Booking = lazy(() => import("./pages/dashboard/booking/booking"));
const Contracts = lazy(() => import("./pages/dashboard/contract/Contracts"));
const Servicerequest = lazy(() => import("./pages/dashboard/servicerequest/servicerequest"));
const Unit = lazy(() => import("./pages/dashboard/Units/unit"));
const Brokers = lazy(() => import("./pages/dashboard/Brokers/brokers"));
const BookingId = lazy(() => import("./pages/dashboard/booking/BookingId"));
const Maintenanceservice = lazy(() => import("./pages/dashboard/MaintenanceService/Maintenanceservice"));
const CancelRequest = lazy(() => import("./pages/dashboard/CancelRequest/CancelRequest"));
const Refundrequest = lazy(() => import("./pages/dashboard/RefundRequest/refundrequest"));
const Banks = lazy(() => import("./pages/dashboard/Banks/banks"));
const GetBanksAccount = lazy(() => import("./pages/dashboard/BankAccount/GetBanksAccount"));
const Service = lazy(() => import("./pages/dashboard/Service/Service"));
const PromoCode = lazy(() => import("./pages/dashboard/PromoCode/PromoCode"));
const SocialMedia = lazy(() => import("./pages/dashboard/SocialMedia/SocialMedia"));
const Setting = lazy(() => import("./pages/dashboard/Setting/Setting"));
const Chats = lazy(() => import("./pages/dashboard/Chats/Chats"));
const Settingtype = lazy(() => import("./pages/dashboard/Settingtype/Settingtype"));
const Brokerpayment = lazy(() => import("./pages/dashboard/Brokerpayment/brokerpayment"));
const Notification = lazy(() => import("./pages/dashboard/Notification/Notification"));
const Features = lazy(() => import("./pages/dashboard/features/features"));
const Compounds = lazy(() => import("./pages/dashboard/compounds/compounds"));
const Unittypes = lazy(() => import("./pages/dashboard/unittypes/unittypes"));
const Placetype = lazy(() => import("./pages/dashboard/placetype/placetype"));
const Identitytypes = lazy(() => import("./pages/dashboard/identitytypes/identitytypes"));
const Unitprices = lazy(() => import("./pages/dashboard/unitprices/unitprices"));
const Country = lazy(() => import("./pages/dashboard/country/country"));
const Cites = lazy(() => import("./pages/dashboard/cities/cites"));
const GetBankOwners = lazy(() => import("./pages/dashboard/BankOwners/GetBankOwners"));
const Paymentmethods = lazy(() => import("./pages/dashboard/paymentmethod/paymentmethods"));
const AddUnit = lazy(() => import("./pages/dashboard/AddUnit/AddUnit"));

// 🎯 LOADING COMPONENT - Professional loading spinner
const PageLoader = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '60vh',
      flexDirection: 'column',
      gap: 2
    }}
  >
    <CircularProgress size={50} sx={{ color: '#60A9C6' }} />
    <Box sx={{ textAlign: 'center', color: '#666' }}>
      Loading...
    </Box>
  </Box>
);

// 🛡️ LAZY ROUTE WRAPPER - Wraps components with loading state
const LazyRoute = ({ children }) => (
  <Suspense fallback={<PageLoader />}>
    {children}
  </Suspense>
);

const checkTokenExpiration = async (navigate) => {
  const currentPath = window.location.pathname; // الحصول على المسار الحالي

  // استثناء الصفحات غير المحمية مثل `/forget`
  if (currentPath === "/") return;

  const token = localStorage.getItem("token");

  if (!token) {
    navigate("/"); // توجيه المستخدم إلى صفحة تسجيل الدخول
    return;
  }

  try {
    const decodedToken = jwtDecode(token);
    const expirationTime = decodedToken.exp * 1000; // وقت انتهاء التوكن
    const currentTime = Date.now();

    const timeRemaining = expirationTime - currentTime;

    if (timeRemaining <= 0) {
      navigate("/"); // التوكن منتهي الصلاحية، توجه إلى صفحة تسجيل الدخول
    } else {
      const refreshTime = timeRemaining - 10 * 60 * 1000; // تحديث التوكن قبل 10 دقائق
      if (refreshTime <= 0) {
        await refreshtoken(); // تحديث التوكن مباشرةً
      } else {
        setTimeout(async () => {
          await refreshtoken();
        }, refreshTime);
      }
    }
  } catch (error) {
    console.error("Invalid token format:", error);
    navigate("/"); // خطأ في التوكن، توجه إلى صفحة تسجيل الدخول
  }
};

function App() {
  const navigate = useNavigate(); // استخدام useNavigate للتنقل بين الصفحات

  useEffect(() => {
    checkTokenExpiration(navigate);
  }, [navigate]);

  useEffect((params) => {
    generateToken();
    onMessage(messaging, (payload) => {
      console.log(payload);
      toast(payload.notification.body);
    });
  }, []);

  return (
    <>
      <Toaster position="top-right" />

      <Routes>
        {/* Login page - loaded eagerly (first page) */}
        <Route path="/" element={<Login />} />
        
        {/* ⚡ All dashboard routes with lazy loading */}
        <Route path="dashboard" element={<Layout children={<LazyRoute><Dashboard /></LazyRoute>} />} />
        <Route path="maintenance" element={<Layout children={<LazyRoute><Maintenance /></LazyRoute>} />} />
        <Route path="maintenanceservice" element={<Layout children={<LazyRoute><Maintenanceservice /></LazyRoute>} />} />
        <Route path="users" element={<Layout children={<LazyRoute><Users /></LazyRoute>} />} />
        <Route path="analysis" element={<Layout children={<LazyRoute><Analysis /></LazyRoute>} />} />
        <Route path="owners" element={<Layout children={<LazyRoute><Owners /></LazyRoute>} />} />
        <Route path="requests" element={<Layout children={<LazyRoute><Requests /></LazyRoute>} />} />
        <Route path="units" element={<Layout children={<LazyRoute><Units /></LazyRoute>} />} />
        <Route path="cancelrequest" element={<Layout children={<LazyRoute><CancelRequest /></LazyRoute>} />} />
        <Route path="refundrequest" element={<Layout children={<LazyRoute><Refundrequest /></LazyRoute>} />} />
        <Route path="bankowners" element={<Layout children={<LazyRoute><GetBankOwners /></LazyRoute>} />} />
        <Route path="banks" element={<Layout children={<LazyRoute><Banks /></LazyRoute>} />} />
        <Route path="features" element={<Layout children={<LazyRoute><Features /></LazyRoute>} />} />
        <Route path="country" element={<Layout children={<LazyRoute><Country /></LazyRoute>} />} />
        <Route path="cites" element={<Layout children={<LazyRoute><Cites /></LazyRoute>} />} />
        <Route path="placetype" element={<Layout children={<LazyRoute><Placetype /></LazyRoute>} />} />
        <Route path="identitytypes" element={<Layout children={<LazyRoute><Identitytypes /></LazyRoute>} />} />
        <Route path="compounds" element={<Layout children={<LazyRoute><Compounds /></LazyRoute>} />} />
        <Route path="unittypes" element={<Layout children={<LazyRoute><Unittypes /></LazyRoute>} />} />
        <Route path="banksaccount" element={<Layout children={<LazyRoute><GetBanksAccount /></LazyRoute>} />} />
        <Route path="insurancerefund" element={<Layout children={<LazyRoute><Refundrequest /></LazyRoute>} />} />
        <Route path="paymentmethods" element={<Layout children={<LazyRoute><Paymentmethods /></LazyRoute>} />} />
        
        {/* Dynamic routes */}
        <Route path="unit/:id" element={<Layout children={<LazyRoute><Unit /></LazyRoute>} />} />
        <Route path="booking/:id" element={<Layout children={<LazyRoute><BookingId /></LazyRoute>} />} />
        
        {/* Other pages */}
        <Route path="payment" element={<Layout children={<LazyRoute><Payment /></LazyRoute>} />} />
        <Route path="contactus" element={<Layout children={<LazyRoute><ContactUs /></LazyRoute>} />} />
        <Route path="changepassword" element={<Layout children={<LazyRoute><ChangePassword /></LazyRoute>} />} />
        <Route path="profile" element={<Layout children={<LazyRoute><Profile /></LazyRoute>} />} />
        <Route path="user" element={<Layout children={<LazyRoute><User /></LazyRoute>} />} />
        <Route path="brokers" element={<Layout children={<LazyRoute><Brokers /></LazyRoute>} />} />
        <Route path="booking" element={<Layout children={<LazyRoute><Booking /></LazyRoute>} />} />
        <Route path="contract" element={<Layout children={<LazyRoute><Contracts /></LazyRoute>} />} />
        <Route path="servicereq" element={<Layout children={<LazyRoute><Servicerequest /></LazyRoute>} />} />
        <Route path="services" element={<Layout children={<LazyRoute><Service /></LazyRoute>} />} />
        <Route path="promo" element={<Layout children={<LazyRoute><PromoCode /></LazyRoute>} />} />
        <Route path="socialmedia" element={<Layout children={<LazyRoute><SocialMedia /></LazyRoute>} />} />
        <Route path="setting" element={<Layout children={<LazyRoute><Setting /></LazyRoute>} />} />
        <Route path="unitprices" element={<Layout children={<LazyRoute><Unitprices /></LazyRoute>} />} />
        <Route path="settingtype" element={<Layout children={<LazyRoute><Settingtype /></LazyRoute>} />} />
        <Route path="notification" element={<Layout children={<LazyRoute><Notification /></LazyRoute>} />} />
        <Route path="chats" element={<Layout children={<LazyRoute><Chats /></LazyRoute>} />} />
        <Route path="brokerpayment" element={<Layout children={<LazyRoute><Brokerpayment /></LazyRoute>} />} />
        <Route path="units/add" element={<Layout children={<LazyRoute><AddUnit /></LazyRoute>} />} />
      </Routes>
    </>
  );
}

export default App;
